
/**
 * Utilidades para la integración con Rewardful
 */

declare global {
  interface Window {
    rewardful: (action: string, data?: Record<string, any>) => void;
    _rwq: string;
    _referralCheckInterval?: NodeJS.Timeout;
  }
}

/**
 * Rastrea una conversión en Rewardful
 * @param email - El correo electrónico del cliente
 * @param options - Opciones adicionales para el seguimiento (opcional)
 */
export const trackConversion = (email: string, options?: Record<string, any>) => {
  try {
    // Verificar si Rewardful está disponible
    if (typeof window === 'undefined' || !window.rewardful) {
      console.warn('[REWARDFUL] Rewardful no está inicializado correctamente');
      return;
    }

    // Prevenir conversiones duplicadas si no es un reintento forzado
    const recentlyTrackedKey = `rewardful_tracked_${email}`;
    const recentlyTracked = sessionStorage.getItem(recentlyTrackedKey);
    
    if (recentlyTracked && !options?.attempt && !options?.forceSend) {
      console.log('[REWARDFUL] Evitando seguimiento duplicado para:', email);
      return;
    }
    
    console.log('[REWARDFUL] Rastreando conversión en Rewardful para:', email);

    // Obtener el ID de referido para incluirlo en los datos
    const referralId = getReferralId();
    
    // Crear un objeto con la estructura correcta para la conversión
    const conversionData: Record<string, any> = {
      email,
      forceSend: true, // Forzamos el envío incluso si Rewardful piensa que ya ha sido enviado
      ...options // Incluir cualquier opción adicional proporcionada
    };

    // Si hay un referralId, asegurarse de incluirlo en los datos de conversión
    if (referralId) {
      // CRÍTICO: client_reference_id es el campo que Rewardful usa para rastrear conversiones
      conversionData.client_reference_id = referralId;
      // También lo incluimos como metadata para cualquier integración personalizada
      conversionData.referral_id = referralId;
      // Añadir información de debug
      conversionData.debug_info = {
        referral_source: 'url_param',
        timestamp: new Date().toISOString()
      };
    }
    
    console.log('[REWARDFUL] Datos de conversión:', conversionData);
    
    // Llamar a la API de Rewardful - asegurarse de que window.rewardful existe
    if (window.rewardful) {
      try {
        window.rewardful('convert', conversionData);
        console.log('[REWARDFUL] Conversión enviada a Rewardful');
      } catch (conversionError) {
        console.error('[REWARDFUL] Error al llamar a window.rewardful:', conversionError);
      }
    } else {
      console.error('[REWARDFUL] window.rewardful no está disponible');
      // Intentar inicializar Rewardful y volver a intentar
      ensureRewardfulInitialized();
      setTimeout(() => {
        if (window.rewardful) {
          window.rewardful('convert', conversionData);
          console.log('[REWARDFUL] Conversión enviada a Rewardful (intento de recuperación)');
        }
      }, 1500);
    }
    
    // Marcar esta conversión como rastreada en esta sesión
    sessionStorage.setItem(recentlyTrackedKey, Date.now().toString());
    
    // Limpiar después de 5 minutos para permitir nuevos seguimientos si es necesario
    setTimeout(() => {
      sessionStorage.removeItem(recentlyTrackedKey);
    }, 5 * 60 * 1000);
  } catch (error) {
    console.error('[REWARDFUL] Error al rastrear conversión en Rewardful:', error);
  }
};

/**
 * Verifica si Rewardful está cargado correctamente
 * @returns boolean indicando si Rewardful está disponible
 */
export const isRewardfulLoaded = (): boolean => {
  return typeof window !== 'undefined' && !!window.rewardful;
};

/**
 * Obtiene el ID de referencia de Rewardful (referral_id) de la URL actual o del localStorage
 * @param checkURLOnly - Si es true, solo busca en la URL, no en localStorage
 * @returns el referral_id si existe en la URL, localStorage o null si no existe
 */
export const getReferralId = (checkURLOnly: boolean = false): string | null => {
  if (typeof window === 'undefined') return null;
  
  // Buscar en la URL actual, comprobando tanto 'ref' como 'via'
  const urlParams = new URLSearchParams(window.location.search);
  let referralId = urlParams.get('via') || urlParams.get('ref') || urlParams.get('referral') || null;
  
  // Si no está en la URL, y se permite buscar en localStorage, buscarlo allí
  if (!referralId && !checkURLOnly && window.localStorage) {
    referralId = localStorage.getItem('rewardful_referral_id');
  }
  
  // Si encontramos un ID de referido, guardémoslo en localStorage para uso futuro
  if (referralId && window.localStorage) {
    localStorage.setItem('rewardful_referral_id', referralId);
    
    // Evitar registros excesivos
    const logCount = sessionStorage.getItem('referral_log_count') || '0';
    if (parseInt(logCount) < 2) {
      console.log('[REWARDFUL] ID de referido guardado en localStorage:', referralId);
      sessionStorage.setItem('referral_log_count', (parseInt(logCount) + 1).toString());
    }
  }
  
  return referralId;
};

/**
 * Crea un objeto con los metadatos de Rewardful para usar con Stripe Checkout
 * @returns un objeto con client_reference_id para pasar a la API de Stripe
 */
export const getRewardfulMetadata = (): Record<string, string> => {
  const referralId = getReferralId();
  const metadata: Record<string, string> = {};
  
  if (referralId) {
    // Esta es la clave para Rewardful - debe ser client_reference_id
    metadata.client_reference_id = referralId;
    // También lo incluimos como metadata para cualquier integración personalizada
    metadata.referral_id = referralId;
  }
  
  return metadata;
};

/**
 * Inicializa manualmente Rewardful si no se ha cargado automáticamente
 * Útil para asegurar que Rewardful esté disponible antes de intentar rastrear conversiones
 */
export const ensureRewardfulInitialized = (): void => {
  if (typeof window !== 'undefined' && !window.rewardful) {
    console.log('[REWARDFUL] Intentando inicializar Rewardful manualmente...');
    try {
      // Intenta cargar el script de Rewardful si no está ya cargado
      const existingScript = document.querySelector('script[src*="getrewardful.com/core.js"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://r.wdfl.co/core.js';
        script.async = true;
        document.head.appendChild(script);
        console.log('[REWARDFUL] Script de Rewardful cargado manualmente');
      }
    } catch (error) {
      console.error('[REWARDFUL] Error al inicializar Rewardful manualmente:', error);
    }
  }
};

/**
 * Asegura que el ID de referido esté en la URL si está disponible en localStorage
 * Esta función es llamada por Layout.tsx y Login.tsx para mantener el referral activo
 */
export const ensureReferralInURL = (): void => {
  if (typeof window === 'undefined') return;
  
  const urlParams = new URLSearchParams(window.location.search);
  const currentReferral = urlParams.get('via') || urlParams.get('ref');
  
  // Si ya hay un referral en la URL, no hacer nada
  if (currentReferral) {
    return;
  }
  
  // Si no hay referral en URL pero sí en localStorage, añadirlo discretamente
  const storedReferral = localStorage.getItem('rewardful_referral_id');
  if (storedReferral) {
    // Solo añadir en páginas importantes como registro o login
    const importantPages = ['/registro', '/login', '/'];
    const currentPath = window.location.pathname;
    
    if (importantPages.includes(currentPath)) {
      urlParams.set('via', storedReferral);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      
      // Usar replaceState para no añadir una nueva entrada al historial
      window.history.replaceState({}, '', newUrl);
      console.log('[REWARDFUL] Referral ID añadido a URL:', storedReferral);
    }
  }
};

/**
 * Genera un enlace de referido para el usuario actual
 * @param userId - El ID del usuario o nombre de usuario
 * @param path - La ruta a la que apuntará el enlace (opcional, por defecto es registro)
 * @param isHomepage - Si es true, genera el enlace hacia la homepage como lo hace Rewardful
 * @param username - Nombre de usuario para usar como código de afiliado en lugar del ID (opcional)
 * @returns Enlace completo con el parámetro de referido
 */
export const generateReferralLink = (
  userId: string, 
  path: string = '/registro', 
  isHomepage: boolean = false,
  username?: string
): string => {
  if (typeof window === 'undefined') return '';
  
  const baseUrl = window.location.origin;
  const targetPath = isHomepage ? '/' : path;
  
  // Usar el nombre de usuario como código de afiliado si está disponible
  // Esto es como lo hace Rewardful (via=username en lugar de via=uuid)
  const referralCode = username || userId;
  
  // Usar el formato 'via' que es el que utiliza Rewardful
  return `${baseUrl}${targetPath}?via=${referralCode}`;
};

/**
 * Verifica si el usuario actual vino de un enlace de referido
 * @returns boolean indicando si el usuario vino de un enlace de referido
 */
export const isUserReferred = (): boolean => {
  return getReferralId() !== null;
};

/**
 * Obtiene el mensaje para compartir con el enlace de referido
 * @returns Texto para compartir en redes sociales
 */
export const getReferralShareMessage = (): string => {
  return '¡Únete a Guía Vegana con mi enlace de referido y obtén un 30% de descuento en tu primer mes!';
};

/**
 * Genera la URL para acceder al dashboard de Rewardful del afiliado
 * @returns URL del dashboard de Rewardful
 */
export const getRewardfulDashboardUrl = (): string => {
  return 'https://guiavegana-com.getrewardful.com';
};

/**
 * Reinicia los contadores de seguimiento de referidos
 * Útil para depuración o después de completar flujos importantes
 */
export const resetReferralTracking = (): void => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.removeItem('referral_redirect_count');
  sessionStorage.removeItem('referral_redirect_in_progress');
  sessionStorage.removeItem('referral_log_count');
  sessionStorage.removeItem('referral_restore_count');
  
  // No eliminamos el ID de referido del localStorage porque queremos mantenerlo entre sesiones
  
  console.log('[REWARDFUL] Contadores de seguimiento de referidos reiniciados');
};

// Inicializar Rewardful al cargar este módulo
setTimeout(ensureRewardfulInitialized, 1500);
